import React, { useMemo } from 'react';
import { DropDown, Label, SelectItem } from '@zaiusinc/hera';
import { Button } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import styles from './DataSyncFormSection.module.sass';
import { LocaleConfig } from '../../../../types/Schema';
import { FilterConfig } from '../../../../types/DataSyncItem';
import {
  expressionToLanguage,
  languageToExpression,
} from '../../../../lib/filterExpression';

/**
 * Sentinel value for an existing expression that this dropdown cannot
 * round-trip (e.g. compound expressions, custom shapes). Surfaced as a disabled
 * option so the user knows a custom filter is in effect rather than seeing a
 * silent default while the underlying value is preserved.
 */
export const CUSTOM_EXPRESSION_SENTINEL = '__CUSTOM_EXPRESSION__';

const FIELD_LANGUAGE = 'language';

const fieldItems: SelectItem[] = [
  { text: 'Language', value: FIELD_LANGUAGE },
];

export interface FiltersSectionProps {
  localeConfig?: LocaleConfig;
  value?: FilterConfig;
  onChange: (value: FilterConfig | undefined) => void;
  disabled?: boolean;
}

const FiltersSection: React.FC<FiltersSectionProps> = ({
  localeConfig,
  value,
  onChange,
  disabled = false,
}) => {
  const supportedLocales = localeConfig?.supported_locales;

  const displayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames(['en'], { type: 'language' });
    } catch {
      return undefined;
    }
  }, []);

  const formatLabel = (code: string): string => {
    const displayName = displayNames?.of(code);
    return displayName && displayName !== code ? `${displayName} (${code})` : code;
  };

  const parsedCode = value ? expressionToLanguage(value.expression) : undefined;
  const hasUnparseableExpression = value !== undefined && parsedCode === undefined;
  const isLanguageFilterAdded = value !== undefined;

  const localeItems: SelectItem[] = useMemo(() => {
    const items: SelectItem[] = (supportedLocales ?? []).map((code) => ({
      text: formatLabel(code),
      value: code,
    }));
    if (hasUnparseableExpression) {
      items.push({
        text: 'Custom filter expression',
        value: CUSTOM_EXPRESSION_SENTINEL,
        disabled: true,
        disabledTooltip:
          'This sync has a filter expression that was set outside this dropdown. ' +
          'Selecting another option will replace it.',
      });
    }
    return items;
  }, [supportedLocales, displayNames, hasUnparseableExpression]);

  if (!supportedLocales || supportedLocales.length === 0) {
    return null;
  }

  const handleSelectLocale = (item: SelectItem) => {
    if (item.value === CUSTOM_EXPRESSION_SENTINEL) {
      return;
    }
    onChange({ expression: languageToExpression(item.value as string) });
  };

  const handleAddLanguageFilter = () => {
    onChange({ expression: languageToExpression(supportedLocales[0]) });
  };

  const handleRemoveLanguageFilter = () => {
    onChange(undefined);
  };

  const selectedLocaleValue = parsedCode ?? CUSTOM_EXPRESSION_SENTINEL;
  const canAddMoreFilters = !isLanguageFilterAdded;

  return (
    <section data-testid="filters-section">
      <Label label="Filters" nonInteractive={true}>
        {isLanguageFilterAdded && (
          <div className={styles.filterRow} data-testid="language-filter-row">
            <DropDown
              items={fieldItems}
              value={FIELD_LANGUAGE}
              onItemSelect={() => undefined}
              width={180}
              disabled={disabled}
            />
            <span className={styles.filterRowOperator}>=</span>
            <DropDown
              items={localeItems}
              value={selectedLocaleValue}
              onItemSelect={handleSelectLocale}
              width={180}
              disabled={disabled}
            />
            {!disabled && (
              <Button
                minimal={true}
                icon={IconNames.CROSS}
                onClick={handleRemoveLanguageFilter}
                aria-label="Remove filter"
                data-testid="remove-language-filter"
              />
            )}
          </div>
        )}

        {canAddMoreFilters && (
          <Button
            icon={IconNames.PLUS}
            text="Add filter"
            minimal={true}
            onClick={handleAddLanguageFilter}
            data-testid="add-filter-button"
            disabled={disabled}
          />
        )}
      </Label>
    </section>
  );
};

export default FiltersSection;
